// Typed static client for printing.
//
// Two surfaces:
//  - The local Scriptly client service at http://localhost:7274 (device discovery
//    + local print). These URLs are ABSOLUTE and deliberately bypass
//    axios.defaults.baseURL — do not route them through the API base.
//  - The backend compound-ingredients print job (/api/Print/...).
//
// This service also owns the local-print orchestration (auth token retrieval,
// job-id generation, printer display-name formatting, and the localhost POST)
// that call sites previously duplicated, so CompoundPrintActions and any future
// caller share one implementation.
//
// getDevices is the one place a service does NOT rethrow: a missing local
// Scriptly client must silently fall back to queue printing, exactly as the page
// did before. All other methods rethrow (PositiveIdService precedent).

import axios from 'axios'
import { compareVersions } from 'compare-versions'

/**
 * Oldest client service build every local-print caller requires. Centralized here so a
 * version bump is one edit rather than one per page.
 */
export const MIN_CLIENT_SERVICE_VERSION = '1.3.0'

/** Oldest client service build that exposes the SignalR scanning hub methods and /scanner endpoint. */
export const MIN_CLIENT_SERVICE_SCANNING_VERSION = '1.4.0'

/** A local printer advertised by the Scriptly client service. */
export interface PrintDevice {
    name: string
    serverName?: string
}

/** Payload posted to the local client service to print a raw printer-language document. */
export interface RawPrintRequest {
    printerName: string
    rawContent: string
    documentName: string
    copies: number
    /**
     * Correlates the shared worker's reply with this request. The client service has no such
     * field and ignores it, which keeps acknowledged printing working against client service
     * builds already installed on workstations.
     */
    jobId?: string
}

/** Payload posted to the local client service to print jobs on a chosen printer. */
export interface LocalPrintRequest {
    printerName: string
    jobs: any
    apiBaseUrl?: string
    markAsPrinted: boolean
    authToken: string | null
    jobId: string
}

/** Backend compound-ingredients print result; toPrint carries local-print jobs. */
export interface CompoundIngredientsPrintResult {
    toPrint?: any[]
}

/** Options for orchestrating a local print of already-fetched jobs. */
export interface LocalPrintOptions {
    /** The selected local printer. */
    printer: PrintDevice
    /** The jobs to print (from the backend print result's toPrint). */
    jobs: any
    /** API base URL for the client service, with any trailing `/api` already stripped. */
    apiBaseUrl?: string
    /** Auth provider type ("Cognito" vs MSAL/OIDC), read from the Vue instance by the caller. */
    authProviderType?: string
    /** localStorage key under which to remember the chosen printer, if any. */
    storageKey?: string
}

const LOCAL_PRINT_BASE = 'http://localhost:7274'

export class PrintService {
    /**
     * Discover local printers from the Scriptly client service. Swallows errors
     * and returns [] — a missing/stopped client silently falls back to queue
     * printing. This non-rethrow is intentional and load-bearing.
     */
    static async getDevices(): Promise<PrintDevice[]> {
        try {
            const response = await axios.get<PrintDevice[]>(`${LOCAL_PRINT_BASE}/print/devices`)
            return response.data
        } catch {
            return []
        }
    }

    /** Send print jobs to the local Scriptly client service. Rethrows on failure. */
    static async sendPrint(request: LocalPrintRequest): Promise<void> {
        await axios.post(`${LOCAL_PRINT_BASE}/print`, request)
    }

    /**
     * Send an already-composed printer-language document (ZPL, PCL) straight to a local
     * printer, bypassing the job pipeline sendPrint feeds. Rethrows on failure.
     */
    static async sendRawPrint(request: RawPrintRequest): Promise<void> {
        await axios.post(`${LOCAL_PRINT_BASE}/print/raw`, request)
    }

    /**
     * Read the installed client service version, or null when it isn't running. Swallows
     * errors for the same reason getDevices does — an absent client is an expected state.
     */
    static async getVersion(): Promise<string | null> {
        try {
            const response = await axios.get<string>(`${LOCAL_PRINT_BASE}/version`)
            return response.data
        } catch {
            return null
        }
    }

    /** Whether a reported client service version is new enough to support local printing. */
    static isSupportedVersion(version: string | null | undefined): boolean {
        return PrintService.meetsMinimumVersion(version, MIN_CLIENT_SERVICE_VERSION)
    }

    /** Whether a reported client service version is new enough to support scanning. */
    static isScanningSupportedVersion(version: string | null | undefined): boolean {
        return PrintService.meetsMinimumVersion(version, MIN_CLIENT_SERVICE_SCANNING_VERSION)
    }

    private static meetsMinimumVersion(version: string | null | undefined, minimum: string): boolean {
        if (!version) {
            return false
        }
        try {
            return compareVersions(version, minimum) >= 0
        } catch {
            // An unparseable version means we can't establish support; treat it as unsupported.
            return false
        }
    }

    /** Whether a printer speaks ZPL, inferred from its name. */
    static isZplPrinter(printer: PrintDevice): boolean {
        const name = printer?.name?.toLowerCase() ?? ''
        return name.includes('zpl') || name.includes('zebra')
    }

    /**
     * Copy of the printer list ordered ZPL/Zebra first, then alphabetically — label callers
     * want the printers that can actually render a label template at the top.
     */
    static sortZplFirst(printers: PrintDevice[]): PrintDevice[] {
        return [...printers].sort((a, b) => {
            const aIsZpl = PrintService.isZplPrinter(a)
            const bIsZpl = PrintService.isZplPrinter(b)
            if (aIsZpl !== bIsZpl) return aIsZpl ? -1 : 1
            return a.name.localeCompare(b.name)
        })
    }

    /** Request the backend compound-ingredients print job (queue or local). */
    static async getCompoundIngredients(rxId: string, localPrint: boolean): Promise<CompoundIngredientsPrintResult> {
        const response = await axios.get<CompoundIngredientsPrintResult>(`/api/Print/${rxId}/compound-ingredients`, {
            params: { localPrint },
        })
        return response.data
    }

    /**
     * Request the backend formula-worksheet print job for a prescription (queue or local). The
     * backend resolves the compound batch behind the prescription and falls back to the blank
     * worksheet, with a notice on the page, when none resolves.
     */
    static async getFormulaWorksheet(rxId: string, localPrint: boolean): Promise<CompoundIngredientsPrintResult> {
        const response = await axios.get<CompoundIngredientsPrintResult>(`/api/Print/${rxId}/formula-worksheet`, {
            params: { localPrint },
        })
        return response.data
    }

    /** Unique job id for a local print request (timestamp + random suffix). */
    static generateJobId(): string {
        return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }

    /**
     * Display name for a local printer: strips the server prefix and backslashes. Callers
     * that aren't falling back to a store printer pass their own no-printer wording.
     */
    static prettyPrinterName(printer: any, noPrinterLabel: string = "Store Default Printer"): string {
        if (!printer) {
            return noPrinterLabel
        }
        if (!printer.serverName) {
            return printer.name
        }
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "")
    }

    /**
     * Resolve a bearer token for the local client service. Picks the Cognito or
     * MSAL/OIDC provider by authProviderType. Never throws — returns null on
     * failure so a token hiccup does not abort a print.
     */
    static async getAuthToken(authProviderType: string | undefined, ensureFresh: boolean = false): Promise<string | null> {
        try {
            if (authProviderType === "Cognito") {
                // Cognito token — ensureFresh forces a refresh if it is expiring soon.
                const { getToken } = await import("@/util/cognitoAuth")
                return await getToken(ensureFresh)
            } else {
                // MSAL/OIDC token — ensureFresh forces a refresh if needed.
                const MicrosoftAuth = (await import("@/util/microsoftAuth")).default
                const token = await MicrosoftAuth.fetchToken(ensureFresh)
                return token.accessToken
            }
        } catch (err) {
            console.error("Failed to get auth token", err)
            return null
        }
    }

    /**
     * Orchestrate a local print of already-fetched jobs: obtain a fresh auth
     * token, generate a job id, POST to the local client service, and remember
     * the chosen printer in localStorage. Rethrows on failure so the caller can
     * surface the error.
     */
    static async sendToLocalPrint(options: LocalPrintOptions): Promise<void> {
        try {
            const printerName = options.printer.name

            // Fresh token so it won't expire during the print job.
            const authToken = await PrintService.getAuthToken(options.authProviderType, true)
            const jobId = PrintService.generateJobId()

            await PrintService.sendPrint({
                printerName,
                jobs: options.jobs,
                apiBaseUrl: options.apiBaseUrl,
                markAsPrinted: false,
                authToken,
                jobId,
            })

            // Remember the selected printer for next time.
            if (options.storageKey) {
                localStorage.setItem(options.storageKey, printerName)
            }
        } catch (error) {
            console.error('Error sending to local printer:', error)
            throw error
        }
    }
}

export default PrintService
